import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Divider,
    Grid,
    Snackbar,
    Alert,
    Chip,
    Tooltip,
    useTheme,
    useMediaQuery,
    CircularProgress,
    InputAdornment,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Print as PrintIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Add as AddIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layouts/DashboardLayout';
import SmartClientSelector from '../components/invoice/SmartClientSelector';
import InlineItemsTable from '../components/invoice/InlineItemsTable';
import InvoiceLivePreview from '../components/invoice/InvoiceLivePreview';
import { getTenantDataService } from '../services/tenant-data-service';
import InvoiceService from '../services/invoiceService';
import NewClientDialog from '../components/dialogs/NewClientDialog';
import NewItemDialog from '../components/dialogs/NewItemDialog';
import { FormControlLabel, Switch } from '@mui/material';
import InvoicePreviewStep from '../components/invoice-steps/InvoicePreviewStep';


interface Client {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    gstin?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    lastInvoiceDate?: Date;
}

interface InventoryItem {
    id: string;
    name: string;
    description?: string;
    price: number;
    hsnCode?: string;
    gstRate?: number;
    unit?: string;
}

interface InvoiceItem {
    id: string;
    inventoryItemId?: string;
    name: string;
    description?: string;
    hsnCode: string;
    quantity: number;
    rate: number;
    gstRate: number;
    discount: number;
    amount: number;
    taxAmount: number;
    total: number;
}

interface InvoiceData {
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    client: Client | null;
    items: InvoiceItem[];
    notes: string;
    terms: string;
}

const ModernInvoiceCreation: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState<Client[]>([]);
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [businessDetails, setBusinessDetails] = useState<any>(null);
    const [showClientDialog, setShowClientDialog] = useState(false);
    const [showItemDialog, setShowItemDialog] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const [displayDueDate, setDisplayDueDate] = useState(true);
    const [showFullScreenPreview, setShowFullScreenPreview] = useState(false);
    const [savedInvoice, setSavedInvoice] = useState<any>(null);
    const [printMenuAnchor, setPrintMenuAnchor] = useState<null | HTMLElement>(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);

    // Invoice data
    const [invoiceData, setInvoiceData] = useState<InvoiceData>({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        client: null,
        items: [],
        notes: '',
        terms: 'Payment is due within 15 days from the date of invoice.',
    });

    // Auto-save state
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error' | 'info' | 'warning',
    });

    // Load data on mount
    useEffect(() => {
        loadData();
    }, [currentUser]);

    const loadData = async () => {
        if (!currentUser?.uid) return;

        try {
            setLoading(true);

            const db = getTenantDataService(currentUser, userProfile);

            // Load business details
            const businesses = await db.getAll<any>('businesses');
            const userBusiness = businesses.find(b => b.userId === currentUser.uid);
            if (userBusiness) {
                setBusinessDetails(userBusiness);
            }

            // Load clients
            const clientsData = await db.getAll<any>('clients');
            const userClients = clientsData.filter(c => c.userId === currentUser.uid);
            setClients(userClients);

            // Load inventory
            const inventoryData = await db.getAll<any>('inventory');
            const userInventory = inventoryData.filter(i => i.userId === currentUser.uid);
            const processedInventory = userInventory.map(item => ({
                id: item.id,
                name: item.name || item.itemName || 'Unnamed',
                description: item.description || '',
                price: item.price || item.rate || item.sellingPrice || 0,
                hsnCode: item.hsnCode || '',
                gstRate: item.gstRate || 18,
                unit: item.unit || 'Nos',
            }));
            setInventory(processedInventory);

            // Generate invoice number
            const invoiceNumber = await InvoiceService.generateInvoiceNumber(
                currentUser.uid,
                userBusiness?.name || 'INV'
            );
            setInvoiceData(prev => ({ ...prev, invoiceNumber }));

        } catch (error) {
            console.error('Error loading data:', error);
            setSnackbar({
                open: true,
                message: 'Error loading data. Please refresh the page.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    // Update invoice data
    const updateInvoiceData = useCallback((updates: Partial<InvoiceData>) => {
        setInvoiceData(prev => ({ ...prev, ...updates }));
        setHasUnsavedChanges(true);
    }, []);

    // Auto-save effect
    useEffect(() => {
        if (!hasUnsavedChanges || !currentUser?.uid) return;

        // Clear existing timer
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        // Set new timer for 30 seconds
        autoSaveTimerRef.current = setTimeout(async () => {
            await handleAutoSave();
        }, 30000);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [invoiceData, hasUnsavedChanges, currentUser]);

    // Auto-save function
    const handleAutoSave = async () => {
        if (!currentUser?.uid) return;

        try {
            await InvoiceService.saveDraft(
                {
                    ...invoiceData,
                    userId: currentUser.uid,
                    status: 'draft',
                },
                currentUser.uid
            );
            setLastSaved(new Date());
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error('Auto-save failed:', error);
        }
    };

    // Save as draft
    const handleSaveDraft = async () => {
        if (!currentUser?.uid) return;

        try {
            setSaving(true);
            await InvoiceService.saveDraft(
                {
                    ...invoiceData,
                    userId: currentUser.uid,
                    status: 'draft',
                },
                currentUser.uid
            );
            setSnackbar({
                open: true,
                message: 'Draft saved successfully!',
                severity: 'success',
            });
            setHasUnsavedChanges(false);
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving draft:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save draft. Please try again.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Save and Preview invoice
    const handleSaveAndPreview = async () => {
        if (!currentUser?.uid) return;

        // Validation
        if (!invoiceData.client) {
            setSnackbar({
                open: true,
                message: 'Please select a client',
                severity: 'warning',
            });
            return;
        }

        if (invoiceData.items.length === 0) {
            setSnackbar({
                open: true,
                message: 'Please add at least one item',
                severity: 'warning',
            });
            return;
        }

        try {
            setSaving(true);

            // Calculate totals
            const subtotal = invoiceData.items.reduce((sum, item) => sum + item.amount, 0);
            const tax = invoiceData.items.reduce((sum, item) => sum + item.taxAmount, 0);
            const discount = invoiceData.items.reduce((sum, item) => sum + item.discount, 0);
            const totalAmount = subtotal + tax - discount;

            // Prepare invoice data matching InvoicePreviewStep expected format
            const invoiceToSave: any = {
                ...invoiceData,
                userId: currentUser.uid,
                status: 'pending',
                client: invoiceData.client.name, // specifically required by preview step
                clientId: invoiceData.client.id,
                clientName: invoiceData.client.name,
                clientDetails: {
                  address: invoiceData.client.address || '',
                  gstin: invoiceData.client.gstin || '',
                  email: invoiceData.client.email || '',
                  phone: invoiceData.client.phone || '',
                  state: invoiceData.client.state || '',
                },
                date: invoiceData.invoiceDate,
                dueDate: invoiceData.dueDate,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                subtotal,
                tax,
                discount,
                amount: totalAmount,
                taxType: businessDetails?.gstin ? 'gst' : 'none',
                taxRate: 18,
                discountType: 'fixed',
                discountValue: discount,
                taxable: !!businessDetails?.gstin,
                termsAndConditions: invoiceData.terms,
            };

            const savedId = await InvoiceService.createInvoice(invoiceToSave, currentUser.uid);
            invoiceToSave.id = savedId;

            setSnackbar({
                open: true,
                message: 'Invoice created successfully!',
                severity: 'success',
            });

            // Show full screen preview instead of navigating away
            setSavedInvoice(invoiceToSave);
            setShowFullScreenPreview(true);

        } catch (error) {
            console.error('Error saving invoice:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save invoice. Please try again.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle client saved
    const handleClientSaved = (newClient: Client) => {
        setShowClientDialog(false);
        // Add to clients list
        setClients(prev => [...prev, newClient]);
        // Select the new client
        updateInvoiceData({ client: newClient });
    };

    // Handle new item saved
    const handleNewItemSaved = (newItem: InventoryItem) => {
        setInventory(prev => [...prev, newItem]);
        setShowItemDialog(false);
        // Add the new item to the invoice items list automatically
        const invoiceItem: InvoiceItem = {
            id: `item-${Date.now()}`,
            inventoryItemId: newItem.id,
            name: newItem.name,
            description: newItem.description,
            hsnCode: newItem.hsnCode || '',
            quantity: 1,
            rate: newItem.price,
            gstRate: newItem.gstRate || 0,
            discount: 0,
            amount: newItem.price,
            taxAmount: (newItem.price * (newItem.gstRate || 0)) / 100,
            total: newItem.price + (newItem.price * (newItem.gstRate || 0)) / 100,
        };
        updateInvoiceData({ items: [...invoiceData.items, invoiceItem] });
        setSnackbar({ open: true, message: 'Item added successfully!', severity: 'success' });
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            // Ctrl+S to save draft
            if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                handleSaveDraft();
            }
            // Ctrl+Enter to save and preview
            if (e.ctrlKey && e.key === 'Enter') {
                e.preventDefault();
                handleSaveAndPreview();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [invoiceData]);

    if (loading) {
        return (
            <DashboardLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    // Build saved-invoice payload for the inline preview
    const buildSavedInvoice = (): any => {
        const subtotal = invoiceData.items.reduce((s, i) => s + i.amount, 0);
        const tax = invoiceData.items.reduce((s, i) => s + i.taxAmount, 0);
        const discount = invoiceData.items.reduce((s, i) => s + i.discount, 0);
        const totalAmount = subtotal + tax - discount;
        return {
            ...invoiceData,
            userId: currentUser?.uid,
            status: 'pending',
            client: invoiceData.client?.name || '',
            clientId: invoiceData.client?.id || '',
            clientName: invoiceData.client?.name || '',
            clientDetails: {
                address: invoiceData.client?.address || '',
                gstin: invoiceData.client?.gstin || '',
                email: invoiceData.client?.email || '',
                phone: invoiceData.client?.phone || '',
                state: invoiceData.client?.state || '',
            },
            date: invoiceData.invoiceDate,
            dueDate: invoiceData.dueDate,
            createdAt: savedInvoice?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            subtotal,
            tax,
            discount,
            amount: totalAmount,
            taxType: businessDetails?.gstin ? 'gst' : 'none',
            taxRate: 18,
            discountType: 'fixed',
            discountValue: discount,
            taxable: !!businessDetails?.gstin,
            termsAndConditions: invoiceData.terms,
            id: savedInvoice?.id,
        };
    };

    return (
        <DashboardLayout>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* Sticky Header */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        borderRadius: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        zIndex: 10,
                        position: 'sticky',
                        top: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => navigate('/invoices')}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    Create Invoice
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : 'Not saved yet'}
                                    {hasUnsavedChanges && ' • Unsaved changes'}
                                </Typography>
                            </Box>
                        </Box>

                    </Box>
                </Paper>

                {/* Scrollable body */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'background.default' }}>
                    {/* Invoice Details */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Invoice Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Invoice Number"
                                    value={invoiceData.invoiceNumber}
                                    onChange={(e) => updateInvoiceData({ invoiceNumber: e.target.value })}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Invoice Date"
                                    type="date"
                                    value={invoiceData.invoiceDate}
                                    onChange={(e) => updateInvoiceData({ invoiceDate: e.target.value })}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {displayDueDate ? (
                                    <TextField
                                        fullWidth
                                        label="Due Date"
                                        type="date"
                                        value={invoiceData.dueDate}
                                        onChange={(e) => updateInvoiceData({ dueDate: e.target.value })}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setDisplayDueDate(false);
                                                            updateInvoiceData({ dueDate: '' });
                                                        }}
                                                        edge="end"
                                                        title="Remove Due Date"
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ) : (
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setDisplayDueDate(true);
                                            const defaultDue = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                                            updateInvoiceData({ dueDate: defaultDue });
                                        }}
                                        fullWidth
                                        sx={{ height: '40px', justifyContent: 'flex-start' }}
                                    >
                                        Add Due Date
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Client Selection */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <SmartClientSelector
                            clients={clients}
                            selectedClient={invoiceData.client}
                            onClientSelect={(client) => updateInvoiceData({ client })}
                            onAddNewClient={() => setShowClientDialog(true)}
                        />
                    </Paper>

                    {/* Items */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Items</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setShowItemDialog(true)}
                            >
                                + New Product
                            </Button>
                        </Box>
                        <InlineItemsTable
                            items={invoiceData.items}
                            inventory={inventory}
                            onItemsChange={(items) => updateInvoiceData({ items })}
                            isGSTApplicable={!!businessDetails?.gstin}
                        />
                    </Paper>

                    {/* Notes & Terms */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Additional Information
                        </Typography>
                        <TextField
                            fullWidth
                            label="Notes"
                            multiline
                            rows={3}
                            value={invoiceData.notes}
                            onChange={(e) => updateInvoiceData({ notes: e.target.value })}
                            placeholder="Any additional notes for the client..."
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Terms & Conditions"
                            multiline
                            rows={3}
                            value={invoiceData.terms}
                            onChange={(e) => updateInvoiceData({ terms: e.target.value })}
                            placeholder="Payment terms, late fees, etc..."
                        />
                    </Paper>

                    {/* ── Inline Invoice Preview ── */}
                    <Paper sx={{ p: 0, mb: 4, overflow: 'hidden' }} elevation={3}>
                        {/* Action bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                px: 3,
                                py: 1.5,
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Invoice Preview
                            </Typography>

                            {/* All action buttons in one row */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {/* Save Draft */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveDraft}
                                    disabled={saving}
                                >
                                    Save Draft
                                </Button>

                                {/* Save Invoice */}
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSaveAndPreview}
                                    disabled={saving}
                                >
                                    Save Invoice
                                </Button>

                                {/* Print dropdown */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PrintIcon />}
                                    onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                                >
                                    Print
                                </Button>
                                <Menu
                                    anchorEl={printMenuAnchor}
                                    open={Boolean(printMenuAnchor)}
                                    onClose={() => setPrintMenuAnchor(null)}
                                >
                                    <MenuItem onClick={() => { setPrintMenuAnchor(null); window.print(); }}>A4 Standard</MenuItem>
                                    <MenuItem onClick={() => { setPrintMenuAnchor(null); window.print(); }}>Thermal 3-inch</MenuItem>
                                    <MenuItem onClick={() => { setPrintMenuAnchor(null); window.print(); }}>Thermal 2-inch</MenuItem>
                                </Menu>

                                {/* Download PDF */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={async () => {
                                        const { exportInvoiceToPdf } = await import('../utils/invoicePdfExporter');
                                        exportInvoiceToPdf('invoice-preview', `Invoice_${invoiceData.invoiceNumber}`);
                                    }}
                                >
                                    Download PDF
                                </Button>

                                {/* Email Invoice */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EmailIcon />}
                                    onClick={() => setEmailDialogOpen(true)}
                                >
                                    Email Invoice
                                </Button>
                            </Box>
                        </Box>

                        {/* InvoicePreviewStep with its own buttons hidden */}
                        <Box sx={{ bgcolor: '#f5f5f5' }}>
                            <InvoicePreviewStep
                                invoiceData={buildSavedInvoice()}
                                businessDetails={businessDetails || {}}
                                onCreateNextInvoice={() => window.location.reload()}
                                hideActionButtons
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* New Client Dialog */}
                <NewClientDialog
                    open={showClientDialog}
                    onClose={() => setShowClientDialog(false)}
                    onSave={handleClientSaved}
                    client={null}
                    userId={currentUser?.uid || ''}
                />

                {/* New Item Dialog */}
                <NewItemDialog
                    open={showItemDialog}
                    onClose={() => setShowItemDialog(false)}
                    onSave={handleNewItemSaved}
                />
            </Box>
        </DashboardLayout>
    );
};

export default ModernInvoiceCreation;
